/** tm/legal/legalTheme.ts — 법령·판례·뉴스 전용 색·스타일·날짜 헬퍼 (ui.tsx 토큰 위에 그룹 색만 추가) */
import type React from 'react';
import { C, T, R } from '../components/ui';
import type { FeedTab } from './legalTypes';

/** 탭별 빈 화면 안내 한 줄 */
export const TAB_HINT: Record<FeedTab, string> = {
  all: '이 주제의 법령 개정·판례·해석·국회 법안·입법예고·뉴스가 날짜순으로 모입니다.',
  law: '감시 법령이 공포·개정되면 여기에 올라옵니다.',
  prec: '법원 판례와 조세심판원·행정심판 결정이 모입니다.',
  expc: '법제처·국세청·부처 유권해석이 모입니다.',
  bill: '22대 국회에 발의된 관련 법률안이 모입니다. 상태가 바뀌면 다시 새 소식이 됩니다.',
  notice: '정부가 시행령·시행규칙 등을 바꾸기 전에 미리 알리는 입법예고가 모입니다.',
  cabinet: '국무회의를 통과한 정부 법령안(시행령·법률안)이 모입니다. 시행령은 곧 공포되고, 법률안은 국회로 넘어갑니다.',
  news: '뉴스는 매일 새벽 모이며, 알림 없이 여기에만 쌓입니다.',
};

/** 그룹 색 (판례·유권해석·심판례·법령·뉴스) */
export const GROUP_COLOR: Record<string, { fg: string; bg: string }> = {
  판례: { fg: '#1E3A8A', bg: '#E0E7FF' },
  유권해석: { fg: '#047857', bg: '#D1FAE5' },
  심판례: { fg: '#9A3412', bg: '#FFEDD5' },
  법령: { fg: '#6D28D9', bg: '#EDE9FE' },
  뉴스: { fg: '#374151', bg: '#F3F4F6' },
  '국회 법안': { fg: '#0F766E', bg: '#CCFBF1' },
  입법예고: { fg: '#9D174D', bg: '#FCE7F3' },
  국무회의: { fg: '#155E75', bg: '#CFFAFE' },
};
/** 국무회의 의결 법령안의 현재 단계 배지 — 공포=초록 · 그 밖(국무회의·공포 대기·국회 제출)=파랑 */
export function cabinetStatus(s: string | null | undefined): { text: string; fg: string; bg: string } {
  const v = s || '';
  if (v === '공포') return { text: '공포', fg: '#047857', bg: '#D1FAE5' };
  if (v === '공포대기') return { text: '공포 대기', fg: C.accent, bg: C.accentLt };
  if (v === '국회제출') return { text: '국회 제출', fg: C.accent, bg: C.accentLt };
  if (/폐기|철회/.test(v)) return { text: v, fg: MUTED, bg: C.bgAlt };
  return { text: '국무회의 의결', fg: C.accent, bg: C.accentLt };
}
/** 국회 법안 상태 배지 색 — 진행 단계(위원회 심사·본회의 부의)=파랑 · 가결=초록 · 계류·폐기·철회·부결=회색 (빨강·주황 없음) */
export function billStatusColor(s: string | null | undefined): { fg: string; bg: string } {
  const v = s || '';
  if (/가결|공포/.test(v)) return { fg: '#047857', bg: '#D1FAE5' };
  if (/폐기|철회|부결|반영/.test(v)) return { fg: MUTED, bg: C.bgAlt };
  if (/본회의|부의|위원회|심사|회부/.test(v)) return { fg: C.accent, bg: C.accentLt };
  return { fg: C.sub, bg: C.bg }; // 계류 등
}
/** 남은 날 배지 색 — 정부지원사업 D-day 와 같은 규칙: 7일 이내 파랑, 그 밖 회색. urgent(마감 D-3 이내)만 빨강 */
export function ddayTone(days: number | null | undefined, urgent = false): { fg: string; bg: string } {
  if (urgent) return { fg: C.red, bg: C.redLt };
  if (days != null && days >= 0 && days <= 7) return { fg: C.accent, bg: C.accentLt };
  return { fg: C.sub, bg: C.bgAlt };
}
/** 법령구분명 → 칩 이름 (대통령령=시행령, 총리령·부령=시행규칙) */
export function lawKindLabel(k: string | null | undefined): string {
  const v = k || '';
  if (!v) return '';
  if (v === '법률') return '법률';
  if (/대통령령|시행령/.test(v)) return '시행령';
  if (/총리령|부령|시행규칙/.test(v)) return '시행규칙';
  return v;
}
/** 입법예고 의견제출 D-day — 남은 날(days_left) 또는 종료일로 계산 */
export function noticeDday(daysLeft: number | null | undefined, end: string | null | undefined): { text: string; open: boolean; urgent: boolean; days: number } | null {
  let d = daysLeft;
  if (d == null && end) d = Math.round((Date.parse(end.slice(0, 10)) - Date.parse(kstToday())) / 86400000);
  if (d == null || Number.isNaN(d)) return null;
  if (d < 0) return { text: '마감', open: false, urgent: false, days: d };
  return { text: d === 0 ? '의견제출 오늘 마감' : `의견제출 D-${d}`, open: true, urgent: d <= 3, days: d };
}
/** 숫자 표기 (천 단위 쉼표) */
export const num = (n: number | null | undefined) => (n == null ? '' : Number(n).toLocaleString('ko-KR'));
export const groupColor = (g: string | null | undefined) => GROUP_COLOR[g || ''] || { fg: C.sub, bg: C.bg };
export const LAW_FG = GROUP_COLOR['법령'].fg;
/** 보조 글자색 — C.muted(#9CA3AF)는 대비가 모자라 글자에는 회색 바탕(#F3F4F6) 위에서도 AA(5.2:1)를 넘는 회색을 쓴다 */
export const MUTED = '#5F6673';
/** 내가 바꾼 제목 글자색 — 사용자 것(내 제목)은 보라 한 가지로 (선택·링크 파랑과 구분) */
export const MINE_FG = C.purple;
/** 시행예정 등 주황 (ui.tsx orange 보다 한 톤 진한 amber — 시안 값) */
export const AMBER = '#D97706';

/** 카드 배지용 짧은 출처명 */
const SHORT: Record<string, string> = {
  prec: '판례', expc: '법제처 해석', decc: '행정심판', ttSpecialDecc: '조세심판원', ntsCgmExpc: '국세청 해석',
  moefCgmExpc: '재경부 해석', mssCgmExpc: '중기부 해석', moelCgmExpc: '고용부 해석', motieCgmExpc: '산업부 해석',
  msitCgmExpc: '과기부 해석', ppsCgmExpc: '조달청 해석', detc: '헌재', law: '법령 개정',
};
export const shortSource = (key: string, label: string) => SHORT[key] || label;

export const TABS: { id: FeedTab; label: string }[] = [
  { id: 'all', label: '전체' }, { id: 'law', label: '법령' }, { id: 'prec', label: '판례·심판' },
  { id: 'expc', label: '유권해석' }, { id: 'bill', label: '국회 법안' }, { id: 'notice', label: '입법예고' }, { id: 'cabinet', label: '국무회의 의결' },
  { id: 'news', label: '뉴스' },
];

/** 법령명 → 핀 카드용 약칭 */
export const shortLaw = (law: string) => law
  .replace('에 관한 특별법', '법').replace('등의 연구개발 지원에 관한 법률', '법').replace('에 관한 법률', '법');

// ── 공통 스타일 ──
/** 칼럼 소제목 — 정부지원사업 화면과 같은 모양 (12px · 600 · 밑줄) */
export const secTitle: React.CSSProperties = { fontSize: T.sm, fontWeight: 600, color: C.sub, margin: '16px 0 8px', paddingBottom: 6, borderBottom: `1px solid ${C.border}` };
/** 칼럼 필터 항목 — 정부지원사업 filterItem 과 같은 모양 */
export const filterItem = (active: boolean): React.CSSProperties => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, width: '100%', border: 0, textAlign: 'left',
  padding: '6px 10px', borderRadius: R.sm, cursor: 'pointer', fontSize: T.sm, fontFamily: 'inherit', marginBottom: 1,
  fontWeight: active ? 600 : 400, color: active ? C.accent : C.text, background: active ? C.accentLt : 'transparent',
});
export const btn = (kind: 'pri' | 'sec' | 'danger' = 'sec'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: R.sm, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  whiteSpace: 'nowrap', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1.5,
  border: `1px solid ${kind === 'pri' ? C.accent : kind === 'danger' ? C.red : C.borderDk}`,
  background: kind === 'pri' ? C.accent : C.white, color: kind === 'pri' ? C.white : kind === 'danger' ? C.red : C.sub,
});
export const chip = (active: boolean): React.CSSProperties => ({
  border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.accentLt : C.white,
  color: active ? C.accent : C.sub, fontWeight: active ? 700 : 400, borderRadius: 12, padding: '3px 9px',
  fontSize: T.xs, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
});
export const newPill: React.CSSProperties = {
  background: C.red, color: C.white, borderRadius: 9, padding: '0 6px', fontSize: T.xs, fontWeight: 700,
  fontVariantNumeric: 'tabular-nums', lineHeight: '16px', display: 'inline-block', minWidth: 16, textAlign: 'center',
};
/** 알림 끈 주제의 새 글 수 (회색) */
export const grayPill: React.CSSProperties = { ...newPill, background: C.bg, color: C.sub, border: `1px solid ${C.border}` };
export const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.sm,
  fontFamily: 'inherit', background: C.white, boxSizing: 'border-box', outline: 'none', color: C.text,
};
export const labelStyle: React.CSSProperties = { fontSize: T.sm, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 4 };

// ── 날짜 (KST 기준) ──
const KST = 9 * 3600 * 1000;
const kst = (ts: string) => { const t = Date.parse(ts); return Number.isNaN(t) ? null : new Date(t + KST); };
export const kstToday = () => new Date(Date.now() + KST).toISOString().slice(0, 10);
/** 'YYYY-MM-DD…' → 'YYYY.MM.DD' (네 자리 연도) */
export const shortDate = (d: string | null | undefined) => (d ? d.slice(0, 10).replace(/-/g, '.') : '-');
/** 타임스탬프 → 'MM.DD HH:mm' (KST) */
export function shortTime(ts: string | null | undefined): string {
  const d = ts ? kst(ts) : null;
  return d ? d.toISOString().slice(5, 16).replace('-', '.').replace('T', ' ') : '';
}
/** 타임스탬프 → 오늘이면 'HH:mm', 아니면 'MM.DD HH:mm' */
export function whenLabel(ts: string | null | undefined): string {
  const d = ts ? kst(ts) : null;
  if (!d) return '';
  const iso = d.toISOString();
  return iso.slice(0, 10) === kstToday() ? `오늘 ${iso.slice(11, 16)}` : shortTime(ts);
}

/** 오늘(KST)부터 날짜까지 남은 날 */
export function daysUntil(d: string | null | undefined): number | null {
  if (!d) return null;
  const diff = Math.round((Date.parse(d.slice(0, 10)) - Date.parse(kstToday())) / 86400000);
  return Number.isNaN(diff) ? null : diff;
}
/** 시행일 D-day */
export function effectiveLabel(eff: string | null | undefined): { text: string; upcoming: boolean; days: number } | null {
  const diff = daysUntil(eff);
  if (diff == null) return null;
  if (diff > 0) return { text: `시행 D-${diff}`, upcoming: true, days: diff };
  if (diff === 0) return { text: '오늘 시행', upcoming: true, days: 0 };
  return { text: '시행 중', upcoming: false, days: diff };
}

/** 법령 현행 원문 링크 (한글주소) */
export const lawUrl = (law: string) => 'https://www.law.go.kr/' + encodeURI(`법령/${law}`);
export const safeHttp = (u: string | null | undefined) => (u && /^https?:\/\//i.test(u) ? u : null);

/** 서버·네트워크 오류 → 사람이 읽는 문장 (날것의 영어·필드명 대신) */
export function errMsg(e: unknown, fb: string): string {
  const m = e instanceof Error ? e.message.trim() : '';
  if (!m) return fb;
  if (/failed to fetch|networkerror|network request|load failed|timeout|aborted/i.test(m)) return '연결이 끊겼습니다. 인터넷 연결을 확인하고 다시 시도해 주세요';
  if (/^(4\d\d|5\d\d)\b|서버 오류|internal|boom|unexpected|syntax/i.test(m)) return `${fb} — 잠시 뒤 다시 시도해 주세요`;
  if (/권한|forbidden|unauthori/i.test(m)) return '이 작업을 할 권한이 없습니다';
  if (/형식 오류|invalid|uuid|[a-z]+Id\b|^[a-z_]+:/i.test(m)) return `${fb} (입력 값을 확인해 주세요)`;
  return /[가-힣]/.test(m) ? m : fb;
}
